<template>
  <div class="bg-white rounded-lg shadow p-6">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold">Report Overview</h2>

      <!-- Quarter selector dropdown -->
      <div class="flex items-center">
        <label for="quarter-select" class="mr-2 text-sm text-gray-500">Select Quarter:</label>
        <select
            id="quarter-select"
            v-model="selectedQuarter"
            @change="onQuarterChange"
            class="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option
              v-for="quarter in availableQuarters"
              :key="quarter.value"
              :value="quarter.value"
          >
            {{ quarter.label }}
          </option>
        </select>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative mb-6">
      <strong class="font-bold">Error:</strong>
      <span class="block sm:inline">{{ error.message }}</span>
    </div>

    <!-- Empty state -->
    <EmptyState
        v-else-if="reports.length === 0"
        icon="document"
        title="No templates found"
        message="There are no forecasting templates available for the selected quarter."
    />

    <!-- Reports list -->
    <div v-else class="space-y-4">
      <!-- Standard view for larger screens -->
      <div v-for="report in reports" :key="report.id" class="border border-gray-200 rounded-lg p-4 hidden md:block">
        <div class="md:flex items-center">
          <!-- Report icon -->
          <div class="flex-shrink-0 mr-4">
            <div class="text-blue-600">
              <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"></path>
              </svg>
            </div>
          </div>

          <!-- Report name -->
          <div class="flex-grow">
            <h3 class="font-medium">{{ report.report_name }}</h3>
            <div v-if="getDocumentDate(report.document_id)" class="text-gray-500 text-sm">
              {{ getDocumentDate(report.document_id)?.type }}:
              {{ getDocumentDate(report.document_id)?.date }}
            </div>
          </div>

          <!-- Quarter -->
          <div class="flex-shrink-0 mx-2 text-center">
            <span class="text-sm">{{ report.quarter }}</span>
          </div>

          <!-- Due date -->
          <div class="flex-shrink-0 mx-2 text-center">
            <span class="text-sm">{{ formatDate(report.due_date) }}</span>
          </div>

          <!-- Status -->
          <div class="flex-shrink-0 mx-2">
            <span
                class="px-2 py-1 text-xs rounded-full"
                :class="getStatusClass(report.status)"
            >
              {{ report.status }}
            </span>
          </div>

          <!-- Action button -->
          <div class="flex-shrink-0 ml-2">
            <button
                v-if="report.status === DOCUMENT_STATUS.READY_FOR_DOWNLOAD || report.status === DOCUMENT_STATUS.DOWNLOADED"
                :class="report.status === DOCUMENT_STATUS.DOWNLOADED
      ? 'border border-blue-600 text-blue-600 bg-transparent px-4 py-2 rounded-md text-sm flex items-center'
      : 'bg-blue-600 text-white px-4 py-2 rounded-md text-sm flex items-center'"
                @click="downloadTemplate(report.template_id)"
            >
              <svg class="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"></path>
              </svg>
              Download
            </button>
            <div v-else class="w-24"></div>
          </div>

          <!-- Expand button -->
          <div class="flex-shrink-0 ml-2">
            <button
                class="text-gray-400 hover:text-gray-600"
                @click="toggleComments(report.document_id)"
            >
              <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Comments section (expanded) -->
        <div v-if="expandedReportId === report.document_id" class="mt-4 pt-4 border-t border-gray-200">
          <CommentList :referenceId="report.document_id" />
        </div>
      </div>

      <!-- Compact view for smaller screens -->
      <div v-for="report in reports" :key="`mobile-${report.id}`" class="border border-gray-200 rounded-lg p-4 md:hidden">
        <div class="flex justify-between items-start mb-3">
          <!-- Report name and icon -->
          <div class="flex items-center">
            <div class="text-blue-600 mr-2">
              <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"></path>
              </svg>
            </div>
            <div>
              <h3 class="font-medium">{{ report.report_name }}</h3>
              <div v-if="getDocumentDate(report.document_id)" class="text-gray-500 text-sm">
                {{ getDocumentDate(report.document_id)?.type }}:
                {{ getDocumentDate(report.document_id)?.date }}
              </div>
            </div>
          </div>

          <!-- Status -->
          <span
              class="px-2 py-1 text-xs rounded-full"
              :class="getStatusClass(report.status)"
          >
            {{ report.status }}
          </span>
        </div>

        <div class="grid grid-cols-2 gap-2 mb-3 text-sm text-gray-600">
          <!-- Quarter -->
          <div>
            <span class="font-medium">Quarter:</span> {{ report.quarter }}
          </div>

          <!-- Due date -->
          <div>
            <span class="font-medium">Due:</span> {{ formatDate(report.due_date) }}
          </div>
        </div>

        <!-- Action buttons -->
        <div class="flex justify-between items-center">
          <button
              class="text-gray-400 hover:text-gray-600"
              @click="toggleComments(report.document_id)"
          >
            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"></path>
            </svg>
          </button>

          <div>
            <button
                v-if="report.status === DOCUMENT_STATUS.READY_FOR_DOWNLOAD"
                class="bg-blue-600 text-white px-3 py-1 rounded-md text-sm flex items-center"
                @click="downloadTemplate(report.template_id)"
            >
              <svg class="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"></path>
              </svg>
              Download
            </button>

            <button
                v-else-if="report.status === DOCUMENT_STATUS.ADJUSTMENT_NEEDED"
                class="bg-purple-600 text-white px-3 py-1 rounded-md text-sm flex items-center"
                @click="reviewReport(report.id)"
            >
              <svg class="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
              </svg>
              Review
            </button>
          </div>
        </div>

        <!-- Comments section (expanded) for mobile -->
        <div v-if="expandedReportId === report.document_id" class="mt-4 pt-4 border-t border-gray-200">
          <CommentList :referenceId="report.document_id" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from 'vue';
import { supabase } from '@/services/supabase';
import { useReports } from '@/composables/useReports';
import { EmptyState } from '@/shared/ui/EmptyState';
import { DOCUMENT_STATUS } from '@/types/document';
import CommentList from '@/components/comments/CommentList.vue';
import { logger, LogCategory } from '@/services/logger';

export default defineComponent({
  name: 'ForecastingTemplateView',
  components: {
    EmptyState,
    CommentList
  },
  emits: ['quarterChanged'],
  props: {
    documentDates: {
      type: Array as () => Array<{
        id: string;
        document_id?: string;
        name: string;
        created_at: string;
        updated_at?: string;
        status?: string;
        dateDisplay?: {
          type: string;
          date: string;
        }
      }>,
      default: () => []
    }
  },
  setup(props, { emit }) {
    const {
      reports,
      loading,
      error,
      fetchReports,
      downloadTemplate,
      reviewReport,
      availableQuarters,
      selectedQuarter,
      changeQuarter
    } = useReports();

    // Track which report has its comments expanded
    const expandedReportId = ref<string | null>(null);

    // Format date to DD/MM/YYYY
    function formatDate(dateString: string): string {
      const date = new Date(dateString);
      return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
    }
    
    // Format date with time for last updated display
    function formatFullDate(dateString: string): string {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    // Get CSS class based on status
    function getStatusClass(status: string): string {
      switch (status) {
        case DOCUMENT_STATUS.READY_FOR_DOWNLOAD:
          return 'bg-blue-100 text-blue-800';
        case DOCUMENT_STATUS.ADJUSTMENT_NEEDED:
          return 'bg-purple-100 text-purple-800';
        case DOCUMENT_STATUS.UPLOADED_CHECKING:
          return 'bg-yellow-100 text-yellow-800';
        case DOCUMENT_STATUS.REVISION_NEEDED:
          return 'bg-green-100 text-green-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    }

    // Handle quarter change
    function onQuarterChange() {
      changeQuarter(selectedQuarter.value);
      // Emit the selected quarter to parent component
      emit('quarterChanged', selectedQuarter.value);
    }

    // Watch for changes in selectedQuarter and emit to parent
    watch(selectedQuarter, (newQuarter) => {
      emit('quarterChanged', newQuarter);
    });
    
    // Cache for document dates to avoid repeated database queries
    const documentDatesCache = ref<Map<string, {type: string, date: string}>>(new Map());
    
    // Function to get document date information directly from the database
    async function fetchDocumentDate(documentId: string) {
      logger.info('Fetching document date from database', LogCategory.UI, { documentId });
      
      try {
        const { data, error } = await supabase
          .from('documents')
          .select('created_at, updated_at')
          .eq('id', documentId)
          .single();
        
        if (error) {
          logger.error('Error fetching document date', LogCategory.UI, {
            documentId,
            error
          });
          return null;
        }
        
        if (data) {
          const result = {
            type: data.updated_at ? 'Last updated' : 'Created',
            date: formatFullDate(data.updated_at || data.created_at)
          };
          
          // Cache the result
          documentDatesCache.value.set(documentId, result);
          
          logger.info('Document date fetched successfully', LogCategory.UI, {
            documentId,
            result
          });
          
          return result;
        }
      } catch (err) {
        logger.error('Exception fetching document date', LogCategory.UI, {
          documentId,
          error: err
        });
      }
      
      return null;
    }
    
    // Function to get document date information
    function getDocumentDate(documentId: string) {
      // Check cache first
      if (documentDatesCache.value.has(documentId)) {
        return documentDatesCache.value.get(documentId);
      }
      
      // Fetch from database if not in cache
      fetchDocumentDate(documentId);
      
      return null;
    }

    // Fetch reports on component mount
    onMounted(async () => {
      await fetchReports();

      // Emit initial value after reports are fetched
      if (selectedQuarter.value) {
        emit('quarterChanged', selectedQuarter.value);
      }
    });

    // Toggle comments visibility for a report
    function toggleComments(reportId: string) {
      logger.info('Toggling comments for report', LogCategory.UI, {
        reportId,
        currentExpandedId: expandedReportId.value,
        action: expandedReportId.value === reportId ? 'collapse' : 'expand'
      });

      if (expandedReportId.value === reportId) {
        expandedReportId.value = null;
      } else {
        expandedReportId.value = reportId;

        // Log the document ID being used for comments
        const report = reports.value.find(r => r.document_id === reportId);
        if (report) {
          logger.info('Expanded report details', LogCategory.UI, {
            reportId: report.document_id,
            reportName: report.report_name,
            reportStatus: report.status
          });
        } else {
          logger.warn('Report not found with ID', LogCategory.UI, { reportId });
        }
      }
    }

    return {
      expandedReportId,
      toggleComments,
      reports,
      loading,
      error,
      formatDate,
      formatFullDate,
      getStatusClass,
      downloadTemplate,
      reviewReport,
      availableQuarters,
      selectedQuarter,
      onQuarterChange,
      DOCUMENT_STATUS,
      getDocumentDate
    };
  }
});
</script>

<style scoped>
/* Add responsive styles for the template view */
@media (max-width: 1280px) {
  /* Adjust spacing in the standard view for medium screens */
  .md\:flex .flex-shrink-0 {
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }

  /* Make the download button more compact */
  .md\:flex button {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
}
</style>
